import unifyGroupOptions from "./unifyGroupOptions";
import unifyGroupVariants from "./unifyGroupVariants";

/**
 * The option values a product form starts on: those of the requested variant,
 * or of the only-default variant, else the first value of every option.
 *
 * @param preferredOptionValues { [optionName]: value } carried over from another
 *   product's selection. Matched per option — values this product doesn't offer
 *   fall back — and outranks `initialVariantId`, which names a variant of the
 *   product the page was rendered for rather than the one being switched to.
 */
export function resolveInitialOptionValues({
  product,
  initialVariantId,
  preferredOptionValues,
}) {
  const variants = unifyGroupVariants(product);

  const defaultOnlyVariant = product?.helpers?.hasOnlyDefaultVariant
    ? variants[0]
    : null;

  const initialVariant = preferredOptionValues
    ? defaultOnlyVariant
    : variants.find(({ id }) => id === initialVariantId) || defaultOnlyVariant;

  if (initialVariant) {
    return Object.fromEntries(
      initialVariant.selectedOptions.map(({ name, value }) => [name, value]),
    );
  }

  return Object.fromEntries(
    unifyGroupOptions(product).map(({ optionName, optionValues }) => {
      const preferred = optionValues.find(
        ({ name }) => name === preferredOptionValues?.[optionName],
      );

      return [optionName, (preferred || optionValues[0])?.name];
    }),
  );
}

// The variant those option values resolve to, matched the same way
// ProductOptions matches its own selection. Lets a collapsed accordion show
// that variant's image and price up front instead of swapping once the options
// mount. Falls back to the first variant so the header still has an image when
// the defaults don't form a valid combination — the case where ProductOptions
// selects nothing and shows its unavailable message.
export default function resolveInitialVariant({ product, initialVariantId }) {
  const variants = unifyGroupVariants(product);
  if (!variants.length) return null;

  const optionValues = resolveInitialOptionValues({
    product,
    initialVariantId,
  });

  return (
    variants.find((variant) =>
      variant.selectedOptions.every(
        ({ name, value }) => optionValues[name] === value,
      ),
    ) || variants[0]
  );
}
